//// pinch-zoom.js — slim pinch/zoom/pan <pinch-zoom> custom element.
////
//// Wraps a single child (plus any of its own children/siblings) and lets the
//// user pan/zoom it with touch pinch, mouse wheel, or drag — built purely on
//// Pointer Events, no legacy mouse/touch fallback.
////
//// Only elements carrying the "pinchable" class start a drag/pinch gesture;
//// the transform itself is applied via CSS custom properties to every direct
//// child, so an image and any overlay elements next to it (tokens, markers,
//// svg overlays, ...) pan/zoom in lockstep.

const MIN_SCALE = 0.01;

const style = document.createElement('style');
style.textContent = `
    pinch-zoom {
        display: block;
        overflow: hidden;
        touch-action: none;
        --scale: 1;
        --x: 0px;
        --y: 0px;
    }
    pinch-zoom > * {
        transform: translate(var(--x), var(--y)) scale(var(--scale));
        transform-origin: 0 0;
        will-change: transform;
    }
    `;
document.head.appendChild(style);

const midpoint = (a, b) => b
    ? { x: (a.clientX + b.clientX) / 2, y: (a.clientY + b.clientY) / 2 }
    : { x: a.clientX, y: a.clientY };
const distance = (a, b) => b ? Math.hypot(b.clientX - a.clientX, b.clientY - a.clientY) : 0;

export class PinchZoom extends HTMLElement {
    constructor() {
        super();
        this._x = 0;
        this._y = 0;
        this._scale = 1;
        this._positioningEl = null;
        this._pointers = new Map();

        new MutationObserver(() => { this._positioningEl = this.children[0] ?? null; })
            .observe(this, { childList: true });

        this.addEventListener('pointerdown', this._onPointerDown.bind(this));
        this._onPointerMove = this._onPointerMove.bind(this);
        this._onPointerEnd = this._onPointerEnd.bind(this);
        this.addEventListener('wheel', this._onWheel.bind(this), { passive: false });
    }

    connectedCallback() {
        this._positioningEl = this.children[0] ?? null;
    }

    get x() { return this._x; }
    get y() { return this._y; }
    get scale() { return this._scale; }

    /** Update the stage with a given scale/x/y, clamped so content can't be panned fully out of view. */
    setTransform({ x = this._x, y = this._y, scale = this._scale } = {}) {
        scale = Math.max(MIN_SCALE, scale);
        const box = this.getBoundingClientRect();
        if (this._positioningEl && box.width && box.height) {
            const content = this._positioningEl.getBoundingClientRect();
            const w = (content.width / this._scale) * scale;
            const h = (content.height / this._scale) * scale;
            if (x > box.width) x = box.width;
            else if (x + w < 0) x = -w;
            if (y > box.height) y = box.height;
            else if (y + h < 0) y = -h;
        }
        if (x === this._x && y === this._y && scale === this._scale) return;
        this._x = x;
        this._y = y;
        this._scale = scale;
        this.style.setProperty('--x', x + 'px');
        this.style.setProperty('--y', y + 'px');
        this.style.setProperty('--scale', scale + '');
        this.dispatchEvent(new Event('change', { bubbles: true }));
    }

    /** Change the scale, keeping the given element-local point (default: content origin) fixed on screen. */
    scaleTo(scale, { originX = 0, originY = 0 } = {}) {
        this._applyChange({
            originX: originX + this._x,
            originY: originY + this._y,
            scaleDiff: scale / this._scale,
        });
    }

    /** Pan/scale relative to the current transform, about an element-local origin point. */
    _applyChange({ panX = 0, panY = 0, originX = 0, originY = 0, scaleDiff = 1 } = {}) {
        this.setTransform({
            scale: this._scale * scaleDiff,
            x: originX + (this._x - originX) * scaleDiff + panX,
            y: originY + (this._y - originY) * scaleDiff + panY,
        });
    }

    _onPointerDown(event) {
        if (event.button !== 0 || !this._positioningEl || this._pointers.size === 2) return;
        if (!event.target.classList.contains('pinchable')) return;
        event.preventDefault();
        this.setPointerCapture(event.pointerId);
        this._pointers.set(event.pointerId, event);
        this.addEventListener('pointermove', this._onPointerMove);
        this.addEventListener('pointerup', this._onPointerEnd);
        this.addEventListener('pointercancel', this._onPointerEnd);
    }

    _onPointerMove(event) {
        if (!this._pointers.has(event.pointerId)) return;
        const previous = [...this._pointers.values()];
        this._pointers.set(event.pointerId, event);
        const current = [...this._pointers.values()];

        const box = this.getBoundingClientRect();
        const prevMid = midpoint(previous[0], previous[1]);
        const currMid = midpoint(current[0], current[1]);
        const prevDist = distance(previous[0], previous[1]);
        const currDist = distance(current[0], current[1]);

        this._applyChange({
            panX: currMid.x - prevMid.x,
            panY: currMid.y - prevMid.y,
            originX: prevMid.x - box.left,
            originY: prevMid.y - box.top,
            scaleDiff: prevDist ? currDist / prevDist : 1,
        });
    }

    _onPointerEnd(event) {
        this._pointers.delete(event.pointerId);
        if (this._pointers.size > 0) return;
        this.removeEventListener('pointermove', this._onPointerMove);
        this.removeEventListener('pointerup', this._onPointerEnd);
        this.removeEventListener('pointercancel', this._onPointerEnd);
    }

    _onWheel(event) {
        event.preventDefault();
        if (!event.ctrlKey) {
            this._applyChange({ panX: -event.deltaX / 2, panY: -event.deltaY / 2 });
            return;
        }
        let { deltaY } = event;
        if (event.deltaMode === 1) deltaY *= 15; // "lines" (Firefox) -> approx pixels
        const box = this.getBoundingClientRect();
        this._applyChange({
            scaleDiff: deltaY > 0 ? 0.95 : 1.05,
            originX: event.clientX - box.left,
            originY: event.clientY - box.top,
        });
    }
}

customElements.define('pinch-zoom', PinchZoom);